import { ThemedContext } from '@/components/marketing/marketing-theme';
import { useState, useEffect, useLayoutEffect, type ComponentType } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ThemeProvider, THEME_REFRESH_EVENT } from '@/components/providers/theme-provider';
import { AuthProvider } from '@/components/providers/auth-provider';
import { LandingPage } from '@/routes/home';
import { ErrorBoundary } from '@/components/error-boundary';
import { Skeleton } from '@/components/ui/skeleton';
import { NotFoundPage } from '@/routes/not-found';
import landingPaths from '@/routes/landing/landing-paths.json';

/**
 * Hydration-safe code-split route.
 *
 * React.lazy ALWAYS suspends on its first render — even when the underlying
 * import() is cached — which commits the Suspense fallback against the
 * prerendered content and fails hydration (React #418, whole-root fallback
 * to client rendering). This wrapper instead:
 *   - renders the page synchronously when its module is cached (preload is
 *     awaited in main.tsx BEFORE hydrateRoot), so hydration matches the
 *     prerendered DOM exactly;
 *   - shows a skeleton and loads the chunk in an effect when navigating
 *     client-side without a preload.
 */
function createLazyRoute(load: () => Promise<ComponentType>) {
  let cached: ComponentType | null = null;
  const preload = () =>
    load().then((page) => {
      cached = page;
    });

  function PreloadedRoute() {
    // Wrap `cached` in arrow functions: React treats a bare function passed to
    // useState/setState as a lazy initializer/updater and CALLS it — executing
    // the page component's hooks outside any render (React #310). The arrow
    // wrapper makes React store the component itself as state.
    const [Page, setPage] = useState<ComponentType | null>(() => cached);
    useEffect(() => {
      if (!Page) preload().then(() => setPage(() => cached));
    }, [Page]);
    if (!Page) {
      return (
        <div className="route-loader flex h-screen items-center justify-center bg-white dark:bg-zinc-950" role="status" aria-label="Loading">
          <div className="flex flex-col items-center gap-5">
            <img width={36} height={36} src="/assets/logo_square.svg" alt="" className="route-loader-mark h-9 w-9 dark:invert" />
            <div className="flex flex-col items-center gap-2.5">
              <Skeleton className="h-3 w-44" />
              <Skeleton className="h-3 w-28" />
            </div>
          </div>
        </div>
      );
    }
    return (
      <ErrorBoundary>
        <Page />
      </ErrorBoundary>
    );
  }

  return { Route: PreloadedRoute, preload, get loaded() { return cached !== null; } };
}

// Route-level code splitting — only the homepage ships in the main bundle.
// Each route below loads its own chunk on demand, keeping the initial
// payload small. The prerenderer waits for network-idle so prerendered
// pages capture fully rendered content.
const PricingPage = createLazyRoute(() => import('@/routes/pricing').then((m) => m.PricingPage));
const DocsPage = createLazyRoute(() => import('@/routes/docs').then((m) => m.DocsPage));
const ChangelogPage = createLazyRoute(() => import('@/routes/changelog').then((m) => m.ChangelogPage));
const StatusPage = createLazyRoute(() => import('@/routes/status').then((m) => m.StatusPage));
const CompareIndexPage = createLazyRoute(() => import('@/routes/compare').then((m) => m.CompareIndexPage));
const ComparisonPageWrapper = createLazyRoute(() => import('@/routes/compare/comparison-wrapper').then((m) => m.ComparisonPageWrapper));
const AuthPage = createLazyRoute(() => import('@/routes/auth').then((m) => m.AuthPage));
const CreateWorkspacePage = createLazyRoute(() => import('@/routes/create-workspace').then((m) => m.CreateWorkspacePage));
const EmbeddedSessionPage = createLazyRoute(() => import('@/routes/embedded-session').then((m) => m.EmbeddedSessionPage));
const PrivacyPage = createLazyRoute(() => import('@/routes/legal').then((m) => m.PrivacyPage));
const TermsPage = createLazyRoute(() => import('@/routes/legal').then((m) => m.TermsPage));
const AboutPage = createLazyRoute(() => import('@/routes/about').then((m) => m.AboutPage));
const ContactPage = createLazyRoute(() => import('@/routes/contact').then((m) => m.ContactPage));
const HelpDeskSoftwareExamplesPage = createLazyRoute(() => import('@/routes/help-desk-software-examples').then((m) => m.HelpDeskSoftwareExamplesPage));
const AiForCustomerServicePage = createLazyRoute(() => import('@/routes/ai-for-customer-service').then((m) => m.AiForCustomerServicePage));
const TicketingSystemExamplesPage = createLazyRoute(() => import('@/routes/ticketing-system-examples').then((m) => m.TicketingSystemExamplesPage));
const AiTicketingSystemPage = createLazyRoute(() => import('@/routes/ai-ticketing-system').then((m) => m.AiTicketingSystemPage));
const LandingRoute = createLazyRoute(() => import('@/routes/landing/landing-route').then((m) => m.LandingRoute));
const LandingV2Route = createLazyRoute(() => import('@/routes/landing-v2/landing-v2-page').then((m) => m.LandingV2Route));
const FreshdeskAlternativePage = createLazyRoute(() => import('@/routes/freshdesk-alternative').then((m) => m.FreshdeskAlternativePage));
const ZendeskAlternativePage = createLazyRoute(() => import('@/routes/zendesk-alternative').then((m) => m.ZendeskAlternativePage));
const BestHelpdeskSoftwarePage = createLazyRoute(() => import('@/routes/best-helpdesk-software').then((m) => m.BestHelpdeskSoftwarePage));
const IssueTrackingForStartupsPage = createLazyRoute(() => import('@/routes/issue-tracking-for-startups').then((m) => m.IssueTrackingForStartupsPage));
const HelpdeskCostCalculatorPage = createLazyRoute(() => import('@/routes/helpdesk-cost-calculator').then((m) => m.HelpdeskCostCalculatorPage));
const PayAsYouGoHelpdeskPage = createLazyRoute(() => import('@/routes/pay-as-you-go-helpdesk').then((m) => m.PayAsYouGoHelpdeskPage));
const HelpdeskAutomationPage = createLazyRoute(() => import('@/routes/helpdesk-automation').then((m) => m.HelpdeskAutomationPage));
const CustomerPortalSoftwarePage = createLazyRoute(() => import('@/routes/customer-portal-software').then((m) => m.CustomerPortalSoftwarePage));
const HelpdeskWithoutPerSeatPricingPage = createLazyRoute(() => import('@/routes/helpdesk-without-per-seat-pricing').then((m) => m.HelpdeskWithoutPerSeatPricingPage));
const InternalTicketingSoftwarePage = createLazyRoute(() => import('@/routes/internal-ticketing-software').then((m) => m.InternalTicketingSoftwarePage));
const KnowledgeBaseSoftwarePage = createLazyRoute(() => import('@/routes/knowledge-base-software').then((m) => m.KnowledgeBaseSoftwarePage));
const PaymentSuccessPage = createLazyRoute(() => import('@/routes/paymentsuccess').then((m) => m.PaymentSuccessPage));
const SlaTrackingSoftwarePage = createLazyRoute(() => import('@/routes/sla-tracking-software').then((m) => m.SlaTrackingSoftwarePage));
const RoadmapPage = createLazyRoute(() => import('@/routes/roadmap').then((m) => m.RoadmapPage));
const PublicHelpCenterPage = createLazyRoute(() => import('@/routes/help-center').then((m) => m.PublicHelpCenterPage));
const HelpCentersDirectoryPage = createLazyRoute(() => import('@/routes/help-centers-directory').then((m) => m.HelpCentersDirectoryPage));
const PortalPage = createLazyRoute(() => import('@/routes/portal').then((m) => m.PortalPage));
const KbPreviewPage = createLazyRoute(() => import('@/routes/kb-preview').then((m) => m.KbPreviewPage));
const KbUnsubscribePage = createLazyRoute(() => import('@/routes/kb-unsubscribe').then((m) => m.KbUnsubscribePage));

// Dashboard — lazy loaded so marketing pages stay fast
const DashboardApp = createLazyRoute(() => import('@/dashboard-app').then((m) => m.default));

/**
 * Preload the chunk for a path BEFORE hydrateRoot runs (see main.tsx).
 * Order matters: '/compare/' must precede '/compare' so comparison pages
 * preload the comparison wrapper rather than the hub.
 */
const ROUTE_CHUNKS: [prefix: string, preload: () => Promise<unknown>][] = [
  ['/dashboard', DashboardApp.preload],
  ['/embedd/', EmbeddedSessionPage.preload],
  ['/create-workspace', CreateWorkspacePage.preload],
  ['/auth', AuthPage.preload],
  ['/compare/', ComparisonPageWrapper.preload],
  ['/compare', CompareIndexPage.preload],
  ['/pricing', PricingPage.preload],
  ['/docs', DocsPage.preload],
  ['/changelog', ChangelogPage.preload],
  ['/status', StatusPage.preload],
  ['/about', AboutPage.preload],
  ['/privacy', PrivacyPage.preload],
  ['/terms', TermsPage.preload],
  ['/contact', ContactPage.preload],
  ['/help-desk-software-examples', HelpDeskSoftwareExamplesPage.preload],
  ['/ai-for-customer-service', AiForCustomerServicePage.preload],
  ['/ticketing-system-examples', TicketingSystemExamplesPage.preload],
  ['/ai-ticketing-system', AiTicketingSystemPage.preload],
  ['/freshdesk-alternative', FreshdeskAlternativePage.preload],
  ['/zendesk-alternative', ZendeskAlternativePage.preload],
  ['/best-helpdesk-software', BestHelpdeskSoftwarePage.preload],
  ['/issue-tracking-for-startups', IssueTrackingForStartupsPage.preload],
  ['/pay-as-you-go-helpdesk', PayAsYouGoHelpdeskPage.preload],
  ['/helpdesk-cost-calculator', HelpdeskCostCalculatorPage.preload],
  ['/helpdesk-automation', HelpdeskAutomationPage.preload],
  ['/paymentsuccess', PaymentSuccessPage.preload],
  ['/sla-tracking-software', SlaTrackingSoftwarePage.preload],
  ['/roadmap', RoadmapPage.preload],
  ['/knowledge-base-software', KnowledgeBaseSoftwarePage.preload],
  ['/customer-portal-software', CustomerPortalSoftwarePage.preload],
  ['/internal-ticketing-software', InternalTicketingSoftwarePage.preload],
  ['/helpdesk-without-per-seat-pricing', HelpdeskWithoutPerSeatPricingPage.preload],
  ['/p/helpdesks', HelpCentersDirectoryPage.preload],
  ['/p/helpcenter/', PublicHelpCenterPage.preload],
  ['/p/helpdesk/', PublicHelpCenterPage.preload],
  ['/p/unsubscribe/', KbUnsubscribePage.preload],
  ['/p/portal/', PortalPage.preload],
];

export function preloadRoute(pathname: string): Promise<unknown> {
  const match = ROUTE_CHUNKS.find(([prefix]) => pathname === prefix || pathname.startsWith(prefix));
  return match ? match[1]() : Promise.resolve();
}

/** `themed` pages have a dark design and follow the visitor's theme (default: system); the rest stay light. */
function MarketingLayout({ children, themed = true }: { children: React.ReactNode; themed?: boolean }) {
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (themed) {
      delete root.dataset.forceLight;
      root.dataset.themed = 'true';
      root.setAttribute('data-mkt', '');
    } else {
      root.dataset.forceLight = 'true';
      root.classList.remove('dark');
    }
    window.dispatchEvent(new Event(THEME_REFRESH_EVENT));
    return () => {
      delete root.dataset.forceLight;
      delete root.dataset.themed;
      root.removeAttribute('data-mkt');
      window.dispatchEvent(new Event(THEME_REFRESH_EVENT));
    };
  }, [themed]);
  return (
    <ThemedContext.Provider value={themed}>
      <div className="min-h-screen warm-page">{children}</div>
    </ThemedContext.Provider>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Marketing pages — home is eager (most traffic), rest lazy */}
      <Route
        path="/"
        element={
          <MarketingLayout>
            <LandingPage />
          </MarketingLayout>
        }
      />
      <Route
        path="/pricing"
        element={
          <MarketingLayout themed>
            <PricingPage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/docs"
        element={
          <MarketingLayout>
            <DocsPage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/changelog"
        element={
          <MarketingLayout>
            <ChangelogPage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/status"
        element={
          <MarketingLayout>
            <StatusPage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/privacy"
        element={
          <MarketingLayout>
            <PrivacyPage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/terms"
        element={
          <MarketingLayout>
            <TermsPage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/about"
        element={
          <MarketingLayout>
            <AboutPage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/contact"
        element={
          <MarketingLayout>
            <ContactPage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/compare"
        element={
          <MarketingLayout themed>
            <CompareIndexPage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/compare/:slug"
        element={
          <MarketingLayout themed>
            <ComparisonPageWrapper.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/help-desk-software-examples"
        element={
          <MarketingLayout>
            <HelpDeskSoftwareExamplesPage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/ai-for-customer-service"
        element={
          <MarketingLayout>
            <AiForCustomerServicePage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/ticketing-system-examples"
        element={
          <MarketingLayout>
            <TicketingSystemExamplesPage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/ai-ticketing-system"
        element={
          <MarketingLayout>
            <AiTicketingSystemPage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/preview/landing/:slug"
        element={
          <MarketingLayout>
            <LandingV2Route.Route />
          </MarketingLayout>
        }
      />
      {(landingPaths as string[]).map((p) => (
        <Route
          key={p}
          path={p}
          element={
            <MarketingLayout>
              <LandingRoute.Route />
            </MarketingLayout>
          }
        />
      ))}
      <Route
        path="/ai-helpdesk-pay-per-resolution"
        element={
          <MarketingLayout>
            <LandingRoute.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/ai-reply-drafting-software"
        element={
          <MarketingLayout>
            <LandingRoute.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/ai-customer-support-agent"
        element={
          <MarketingLayout>
            <LandingRoute.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/freshdesk-alternative"
        element={
          <MarketingLayout>
            <FreshdeskAlternativePage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/zendesk-alternative"
        element={
          <MarketingLayout>
            <ZendeskAlternativePage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/best-helpdesk-software"
        element={
          <MarketingLayout>
            <BestHelpdeskSoftwarePage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/issue-tracking-for-startups"
        element={
          <MarketingLayout>
            <IssueTrackingForStartupsPage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/helpdesk-cost-calculator"
        element={
          <MarketingLayout themed>
            <HelpdeskCostCalculatorPage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/pay-as-you-go-helpdesk"
        element={
          <MarketingLayout>
            <PayAsYouGoHelpdeskPage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/sla-tracking-software"
        element={
          <MarketingLayout>
            <SlaTrackingSoftwarePage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/helpdesk-automation"
        element={
          <MarketingLayout>
            <HelpdeskAutomationPage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/knowledge-base-software"
        element={
          <MarketingLayout>
            <KnowledgeBaseSoftwarePage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/customer-portal-software"
        element={
          <MarketingLayout>
            <CustomerPortalSoftwarePage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/helpdesk-without-per-seat-pricing"
        element={
          <MarketingLayout>
            <HelpdeskWithoutPerSeatPricingPage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/internal-ticketing-software"
        element={
          <MarketingLayout>
            <InternalTicketingSoftwarePage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/roadmap"
        element={
          <MarketingLayout>
            <RoadmapPage.Route />
          </MarketingLayout>
        }
      />
      <Route
        path="/paymentsuccess"
        element={
          <AuthProvider>
            <PaymentSuccessPage.Route />
          </AuthProvider>
        }
      />
      <Route
        path="/auth"
        element={<AuthPage.Route />}
      />
      <Route
        path="/create-workspace"
        element={<CreateWorkspacePage.Route />}
      />

      {/* Embedded Sessions — standalone, no auth guard or marketing layout */}
      <Route
        path="/embedd/:session_token"
        element={<EmbeddedSessionPage.Route />}
      />
      <Route
        path="/embedd/:session_token/*"
        element={<EmbeddedSessionPage.Route />}
      />

      {/* Public Helpdesk pages — standalone (own theming, no auth) */}
      <Route
        path="/p/helpdesk/preview/:token"
        element={<KbPreviewPage.Route />}
      />
      <Route
        path="/p/portal/:ident/*"
        element={<PortalPage.Route />}
      />
      <Route
        path="/p/unsubscribe/:token"
        element={<KbUnsubscribePage.Route />}
      />
      <Route
        path="/p/helpdesk/:ident"
        element={<PublicHelpCenterPage.Route />}
      />
      <Route
        path="/p/helpcenter/:ident"
        element={<PublicHelpCenterPage.Route />}
      />
      <Route
        path="/p/helpcenter/:ident/:slug"
        element={<PublicHelpCenterPage.Route />}
      />
      <Route
        path="/p/helpdesk/:ident/:slug"
        element={<PublicHelpCenterPage.Route />}
      />
      <Route
        path="/p/helpdesks"
        element={<HelpCentersDirectoryPage.Route />}
      />
      <Route
        path="/p/helpdesk"
        element={<HelpCentersDirectoryPage.Route />}
      />

      {/* Dashboard — lazy loaded */}
      <Route
        path="/dashboard/*"
        element={<DashboardApp.Route />}
      />

      {/* Catch-all → 404 page */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
